"use client";
import { TokenInfo } from "@portal/app/_common/lib/token";
import useMediaQuery, { UseMediaQueryOptions } from "@mui/material/useMediaQuery";
import type { Theme } from "@mui/material/styles";
import { LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT } from "@portal/app/_components/ui-elements/Layout";
import { Role } from "@portal/app/_common/types/role";
import {
  AuthenticatedResult,
  MaybeAuthenticatedResult,
} from "@portal/app/_server/serverComponentHelpers";
import {
  EXPERIMENTS,
  ExperimentValueDescription,
  Experiments,
  getClientExperiments,
  getExperimentValueFromDescription,
} from "@portal/app/_common/lib/experiments";
import { DeviceInfo } from "@portal/app/_common/lib/requestUtils";
import { createGenericContext } from "@portal/app/_common/lib/createGenericContext";
import { useCallback } from "react";

export type AppPageProps = AuthenticatedResult<Role> | MaybeAuthenticatedResult<Role>;

export type PageContextProps = {
  token: TokenInfo | null;
  experiments: Experiments;
  hasExperimentValue: <AE extends typeof EXPERIMENTS, E extends keyof AE = keyof AE>(
    experiment: E,
    valueDescription: ExperimentValueDescription<AE, E>
  ) => boolean;
  isMobile: boolean;
  isDesktop: boolean;
  mobileMatchMedia: UseMediaQueryOptions;
  desktopMatchMedia: UseMediaQueryOptions;
  clientLocation: {
    country: string | undefined;
    city: string | undefined;
    state: string | undefined;
    zip: string | undefined;
  };
};

const [usePageContext, PageContext] = createGenericContext<PageContextProps>("PageContext");
export { usePageContext, PageContext };

export const PageProvider = ({
  children,
  tokenInfo,
  deviceInfo,
  experiments,
  cfGeolocation,
}: React.PropsWithChildren<{
  tokenInfo: TokenInfo | undefined;
  deviceInfo: DeviceInfo | null;
  experiments: Experiments | undefined;
  cfGeolocation: {
    cfCountry: string | undefined;
    cfCity: string | undefined;
    cfState: string | undefined;
    cfZip: string | undefined;
  };
}>) => {
  const mobileMatchMedia: UseMediaQueryOptions = {
    ssrMatchMedia: () => ({
      matches: deviceInfo?.isMobile ?? true,
    }),
  };
  const desktopMatchMedia: UseMediaQueryOptions = {
    ssrMatchMedia: () => ({
      matches: deviceInfo?.isDesktop ?? false,
    }),
  };
  const isMobileMediaQuery = useMediaQuery(
    (theme: Theme) => theme.breakpoints.down(LAYOUT_MOBILE_DESKTOP_RESPONSIVE_BREAKPOINT),
    mobileMatchMedia
  );

  const hasExperimentValue = useCallback(
    <AE extends typeof EXPERIMENTS, E extends keyof AE = keyof AE>(
      experiment: E,
      valueDescription: ExperimentValueDescription<AE, E>
    ) =>
      ((experiments ?? getClientExperiments<AE, E>()) as AE)[experiment] ===
      getExperimentValueFromDescription(experiment, valueDescription),
    [experiments]
  );

  return (
    <PageContext.Provider
      value={{
        mobileMatchMedia,
        desktopMatchMedia,
        isMobile: isMobileMediaQuery,
        isDesktop: !isMobileMediaQuery,
        token: tokenInfo ?? null,
        experiments: experiments ?? getClientExperiments(),
        hasExperimentValue,
        clientLocation: {
          country: cfGeolocation.cfCountry,
          city: cfGeolocation.cfCity,
          state: cfGeolocation.cfState,
          zip: cfGeolocation.cfZip,
        },
      }}
    >
      {children}
    </PageContext.Provider>
  );
};
